import { ReactNode } from 'react'
import Link from 'next/link'

type Variant = 'primary' | 'outline' | 'ghost' | 'inverse'
type Size = 'md' | 'lg'

const base =
  'inline-flex items-center justify-center font-medium rounded-pill transition duration-200 ease-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'

const variantClasses: Record<Variant, string> = {
  primary: 'bg-brand-900 text-white hover:bg-brand-800 active:bg-brand-950',
  outline:
    'border border-line text-ink-primary hover:border-brand-900 hover:bg-surface-subtle',
  ghost: 'text-brand-900 hover:bg-surface-subtle',
  inverse: 'bg-white text-brand-900 hover:bg-brand-50',
}

const sizeClasses: Record<Size, string> = {
  md: 'px-5 py-2.5 text-body-sm',
  lg: 'px-7 py-3.5 text-body-md',
}

interface ButtonProps {
  children: ReactNode
  href?: string
  variant?: Variant
  size?: Size
  type?: 'button' | 'submit' | 'reset'
  onClick?: (e: React.MouseEvent) => void
  className?: string
  disabled?: boolean
  target?: '_blank' | '_self'
  rel?: string
}

export default function Button({
  children,
  href,
  variant = 'primary',
  size = 'md',
  type = 'button',
  onClick,
  className = '',
  disabled = false,
  target,
  rel,
}: ButtonProps) {
  const classes = `${base} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`

  if (href) {
    if (target === '_blank') {
      return (
        <a
          href={href}
          target={target}
          rel={rel ?? 'noopener noreferrer'}
          className={classes}
          onClick={onClick}
        >
          {children}
        </a>
      )
    }
    // Static assets (a file extension in the last segment) must bypass
    // next/link: it locale-prefixes the href (/tr/downloads/... → 404).
    if (/\.[a-z0-9]+$/i.test(href.split('/').pop() ?? '')) {
      return (
        <a href={href} className={classes} onClick={onClick}>
          {children}
        </a>
      )
    }
    return (
      <Link href={href} className={classes} onClick={onClick}>
        {children}
      </Link>
    )
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classes}
    >
      {children}
    </button>
  )
}
